import type { KybCompanyDetailsData } from '@tasama/types';
import type { ExtendedCompanyDetailsFormValues } from '@/schemas/company-details.schema';
import {
  INDUSTRY_SECTORS,
  EMPLOYEE_COUNT_OPTIONS,
  ANNUAL_REVENUE_OPTIONS,
} from '@/schemas/company-details.schema';

/**
 * Maps company details from the shared store to form data format
 * Converts display values back to option values for dropdowns
 */
export const mapCompanyDetailsToFormData = (
  companyDetails: KybCompanyDetailsData | null
): Partial<ExtendedCompanyDetailsFormValues> => {
  if (!companyDetails) {
    return {};
  }

  // Helper function to find value by label
  const findValueByLabel = (
    options: Array<{ value: string; label: string }>,
    label: string
  ): string => {
    const option = options.find((opt) => opt.label === label);
    return option ? option.value : label;
  };

  // Map industry from label back to value
  const industryValue = companyDetails.industry
    ? findValueByLabel(INDUSTRY_SECTORS, companyDetails.industry)
    : '';

  // Helper function to convert various date formats to DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateString: string): string => {
    if (!dateString) return '';

    // Already in DD/MM/YYYY format — return as-is
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }

    // ISO date (YYYY-MM-DD) or ISO datetime — extract date part via string split
    // to avoid new Date() timezone-shifting the date (e.g. "2025-01-05" parsed as
    // UTC midnight becoming Jan 4 in UTC+3 via local-time getDate())
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    }

    return '';
  };

  return {
    companyName: companyDetails.companyName || '',
    crNumber: companyDetails.crNumber || '',
    website: companyDetails.website || '',
    industrySector: industryValue,
    country: companyDetails.country || '',
    countryOfHeadquarter:
      companyDetails.countryOfHeadquarter || companyDetails.country || '',
    address: companyDetails.addressLine1 || '',
    city: companyDetails.city || '',
    postalCode: companyDetails.postalCode || '',
    numberOfEmployees: companyDetails.numberOfEmployees,
    annualRevenue: companyDetails.annualRevenue,
    registrationStartDate: formatDateToDDMMYYYY(
      companyDetails.registrationStartDate || ''
    ),
  };
};

/**
 * Maps form data back to company details format for API submission
 * Note: Country field should always be sent as code (value), not label
 *
 * @param formData - The form data to map
 * @param countriesList - Optional list of countries to convert country names to codes
 */
export const mapFormDataToCompanyDetails = (
  formData: Partial<ExtendedCompanyDetailsFormValues>,
  countriesList?: Array<{ value: string; label: string }>
): Partial<KybCompanyDetailsData> => {
  // Helper function to find label by value
  const findLabelByValue = (
    options: Array<{ value: string; label: string }>,
    value: string
  ): string => {
    const tempValue = value.replace('–', '-');
    const option = options.find((opt) => opt.value === tempValue);
    return option ? option.label : tempValue;
  };

  // Helper function to find value (code) by label (name)
  const findValueByLabel = (
    options: Array<{ value: string; label: string }>,
    label: string
  ): string => {
    const option = options.find((opt) => opt.label === label);
    return option ? option.value : label;
  };

  // Map industry from value back to label
  const industryLabel = formData.industrySector
    ? findLabelByValue(INDUSTRY_SECTORS, formData.industrySector)
    : '';

  // Map employee count from value back to label
  const employeeCountLabel = formData.numberOfEmployees
    ? findLabelByValue(EMPLOYEE_COUNT_OPTIONS, formData.numberOfEmployees)
    : '';

  const annualRevenueLabel = formData.annualRevenue
    ? findLabelByValue(ANNUAL_REVENUE_OPTIONS, formData.annualRevenue)
    : '';

  // For country field, always send the value (code) not the label
  // The form may have either country or countryOfHeadquarter depending on company type
  const countryFieldValue =
    formData.countryOfHeadquarter || formData.country || '';

  // Convert country name to code if countriesList is provided
  // This handles the case where dropdown is disabled and displays country name
  // but we need to send country code to backend
  let countryValue = countryFieldValue;
  if (countriesList && countriesList.length > 0 && countryFieldValue) {
    // First check if the value is already a code (2-letter uppercase)
    const isAlreadyCode = /^[A-Z]{2}$/.test(countryFieldValue);

    if (!isAlreadyCode) {
      // It's a country name, convert to code
      countryValue = findValueByLabel(countriesList, countryFieldValue);
    }
  }

  return {
    companyName: formData.companyName || '',
    crNumber: formData.crNumber || '',
    website: formData.website || '',
    industry: industryLabel,
    addressLine1: formData.address || '',
    city: formData.city || '',
    postalCode: formData.postalCode || '',
    country: countryValue,
    registrationStartDate: formData.registrationStartDate || '',
    numberOfEmployees: employeeCountLabel,
    annualRevenue: annualRevenueLabel,
  };
};
